import { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Shield, ShieldCheck, ShieldOff, Copy, CheckCircle, AlertTriangle } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

export default function TenantSecurity() {
  const { user } = useAuth();
  const [platformUser, setPlatformUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Setup state
  const [setupMode, setSetupMode] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [showBackupCodes, setShowBackupCodes] = useState(false);

  // Disable state
  const [disableMode, setDisableMode] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');

  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get<{ platformUser: any }>('/auth/me')
      .then(data => setPlatformUser(data.platformUser))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const is2FAEnabled = platformUser?.totp_enabled === true;

  async function handleSetup() {
    setActionLoading(true);
    setError('');
    try {
      const data = await api.post<{ qr_code: string; secret: string }>('/auth/2fa/setup');
      setQrCode(data.qr_code);
      setSecret(data.secret);
      setSetupMode(true);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setActionLoading(false);
    }
  }

  async function handleConfirm() {
    if (!verifyCode) { setError('Vul de code in.'); return; }
    setActionLoading(true);
    setError('');
    try {
      const data = await api.post<{ ok: boolean; backup_codes: string[] }>('/auth/2fa/confirm', { totp_code: verifyCode });
      setBackupCodes(data.backup_codes);
      setShowBackupCodes(true);
      setSetupMode(false);
      setPlatformUser({ ...platformUser, totp_enabled: true });
      setSuccess('Tweestapsverificatie is ingeschakeld!');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setActionLoading(false);
    }
  }

  async function handleDisable() {
    if (!disablePassword) { setError('Vul je wachtwoord in.'); return; }
    setActionLoading(true);
    setError('');
    try {
      await api.post('/auth/2fa/disable', { password: disablePassword });
      setPlatformUser({ ...platformUser, totp_enabled: false });
      setDisableMode(false);
      setDisablePassword('');
      setSuccess('Tweestapsverificatie is uitgeschakeld.');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setActionLoading(false);
    }
  }

  function copyBackupCodes() {
    navigator.clipboard.writeText(backupCodes.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  if (loading) {
    return <div className="space-y-4">{[...Array(3)].map((_, i) => <div key={i} className="h-16 bg-gray-50 rounded-xl animate-pulse" />)}</div>;
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Beveiliging</h1>
        <p className="text-sm text-gray-500 mt-1">Tweestapsverificatie (2FA) voor je account</p>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" /> {error}
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
          <CheckCircle className="w-4 h-4 flex-shrink-0" /> {success}
        </div>
      )}

      {/* Status card */}
      <div className="card p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${is2FAEnabled ? 'bg-green-100' : 'bg-gray-100'}`}>
              {is2FAEnabled ? <ShieldCheck className="w-5 h-5 text-green-600" /> : <Shield className="w-5 h-5 text-gray-400" />}
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Tweestapsverificatie</h2>
              <p className="text-sm text-gray-500">
                {is2FAEnabled ? 'Ingeschakeld — je account is extra beveiligd' : 'Uitgeschakeld — schakel in voor extra beveiliging'}
              </p>
            </div>
          </div>
          {!setupMode && !disableMode && !showBackupCodes && (
            is2FAEnabled ? (
              <button onClick={() => setDisableMode(true)} className="btn-secondary text-sm flex items-center gap-1.5">
                <ShieldOff className="w-4 h-4" /> Uitschakelen
              </button>
            ) : (
              <button onClick={handleSetup} disabled={actionLoading} className="btn-primary text-sm flex items-center gap-1.5">
                <Shield className="w-4 h-4" /> {actionLoading ? 'Laden...' : 'Inschakelen'}
              </button>
            )
          )}
        </div>
      </div>

      {/* Setup flow */}
      {setupMode && (
        <div className="card p-6 space-y-5">
          <h3 className="text-base font-semibold text-gray-900">2FA instellen</h3>
          <p className="text-sm text-gray-600">
            Scan de QR-code met een authenticator app (zoals Google Authenticator, Authy of 1Password).
          </p>

          <div className="flex justify-center p-4 bg-white rounded-lg border">
            {qrCode && <img src={qrCode} alt="QR Code" className="w-48 h-48" />}
          </div>

          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500 mb-1">Of voer deze code handmatig in:</p>
            <code className="text-sm font-mono text-gray-900 break-all">{secret}</code>
          </div>

          <div>
            <label className="label">Verificatiecode</label>
            <input
              type="text"
              value={verifyCode}
              onChange={e => setVerifyCode(e.target.value.replace(/\s/g, ''))}
              className="input text-center text-lg tracking-widest font-mono"
              placeholder="000000"
              maxLength={6}
              autoFocus
            />
          </div>

          <div className="flex gap-3">
            <button onClick={handleConfirm} disabled={actionLoading || !verifyCode} className="btn-primary text-sm">
              {actionLoading ? 'Verifying...' : 'Bevestigen'}
            </button>
            <button onClick={() => { setSetupMode(false); setError(''); }} className="btn-secondary text-sm">
              Annuleren
            </button>
          </div>
        </div>
      )}

      {/* Backup codes display */}
      {showBackupCodes && (
        <div className="card p-6 space-y-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            <h3 className="text-base font-semibold text-gray-900">Bewaar je backup-codes</h3>
          </div>
          <p className="text-sm text-gray-600">
            Bewaar deze codes op een veilige plek. Je kunt ze gebruiken als je geen toegang hebt tot je authenticator app.
            Elke code is eenmalig bruikbaar.
          </p>

          <div className="grid grid-cols-2 gap-2 p-4 bg-gray-50 rounded-lg font-mono text-sm">
            {backupCodes.map((code, i) => (
              <div key={i} className="py-1 text-gray-900">{code}</div>
            ))}
          </div>

          <div className="flex gap-3">
            <button onClick={copyBackupCodes} className="btn-secondary text-sm flex items-center gap-1.5">
              {copied ? <><CheckCircle className="w-4 h-4" /> Gekopieerd</> : <><Copy className="w-4 h-4" /> Kopieer codes</>}
            </button>
            <button onClick={() => setShowBackupCodes(false)} className="btn-primary text-sm">
              Ik heb de codes bewaard
            </button>
          </div>
        </div>
      )}

      {/* Disable flow */}
      {disableMode && (
        <div className="card p-6 space-y-4">
          <h3 className="text-base font-semibold text-gray-900">2FA uitschakelen</h3>
          <p className="text-sm text-gray-600">
            Voer je wachtwoord in om tweestapsverificatie uit te schakelen.
          </p>
          <div>
            <label className="label">Wachtwoord</label>
            <input
              type="password"
              value={disablePassword}
              onChange={e => setDisablePassword(e.target.value)}
              className="input"
              autoFocus
            />
          </div>
          <div className="flex gap-3">
            <button onClick={handleDisable} disabled={actionLoading || !disablePassword} className="btn-danger text-sm">
              {actionLoading ? 'Bezig...' : 'Uitschakelen'}
            </button>
            <button onClick={() => { setDisableMode(false); setError(''); }} className="btn-secondary text-sm">
              Annuleren
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
